import { ActivatedRouteSnapshot } from '@angular/router';
import { EChartsOption } from 'echarts';
import moment from 'moment';
import { Account } from 'src/app/auth/models/user.model';
import { InquiryDependent } from 'src/app/pages/student-inquiry/models';
import { EMPTY_DATE_TIME } from 'src/app/request-information/models/inquiry.model';
import { Constants } from '../constants';
import { DateUtils } from './date.utils';

export class CommonUtils {
  static getFullName(firstName: string | undefined, lastName: string | undefined): string {
    return `${firstName || ''} ${lastName || ''}`;
  }

  static getInitials(firstName: string | undefined, lastName: string | undefined): string {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`;
  }

  static getInitialsUsingFullName(name: string | undefined): string {
    if (name?.includes(' ')) {
      return `${name?.split(' ')[0].charAt(0) || ''}${name?.split(' ')[1].charAt(0) || ''}`;
    } else {
      return `${name?.charAt(0)}`;
    }
  }

  static getInquiryContactMissingFields(inquiry: { phoneNo?: string; preferredContactMethod?: number }): Array<string> {
    const { inquiryRequiredFields } = Constants;
    return [!inquiry.phoneNo && inquiryRequiredFields.phoneNo, !inquiry.preferredContactMethod && inquiryRequiredFields.preferredContactMethod].filter(
      (field): field is string => !!field
    );
  }

  static getInquiryAccountHolderMissingFields(inquiry: { dateOfBirth?: string; locationId?: number }): Array<string> {
    const { inquiryRequiredFields } = Constants;
    return [
      (!inquiry.dateOfBirth || inquiry.dateOfBirth.startsWith(EMPTY_DATE_TIME)) && inquiryRequiredFields.accountHolderDateOfBirth,
      !inquiry.locationId && inquiryRequiredFields.accountHolderLocationId
    ].filter((field): field is string => !!field);
  }

  static getInquiryStudentMissingFields(student: InquiryDependent): Array<string> {
    const { inquiryRequiredFields } = Constants;
    return [
      (!student.dateOfBirth || student.dateOfBirth.startsWith(EMPTY_DATE_TIME)) && inquiryRequiredFields.dateOfBirth,
      !student.locationId && inquiryRequiredFields.locationId,
      !student.typeOfInquiry && inquiryRequiredFields.typeOfInquiry,
      !student.instruments?.some((instrument) => instrument.instrumentId) && inquiryRequiredFields.instrument
    ].filter((field): field is string => !!field);
  }

  static getIncompleteDataTooltip(missingFields: Array<string> | undefined): string {
    const { errorMessages } = Constants;
    return missingFields?.length ? errorMessages.inquiryMissingFields.replace('{fields}', [...new Set(missingFields)].join(', ')) : errorMessages.inquiryIncomplete;
  }

  static getPlainTextFromHtml(html: string): string {
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  }

  static isSameText(value?: string, otherValue?: string): boolean {
    const normalize = (text?: string) => text?.trim().toLowerCase() ?? '';
    const normalized = normalize(value);
    return !!normalized && normalized === normalize(otherValue);
  }

  static getSlotInterval(scheduleDate: string, startTime: string, endTime: string): { date: string; start: number; end: number } {
    const toMinutes = (time: string) => moment(time).hours() * 60 + moment(time).minutes();
    return { date: moment(scheduleDate).format('YYYY-MM-DD'), start: toMinutes(startTime), end: toMinutes(endTime) };
  }

  static getPageTitle(routeSnapshot: ActivatedRouteSnapshot) {
    let title: string = routeSnapshot.data && routeSnapshot.data['pageTitle'] ? routeSnapshot.data['pageTitle'] : 'OMS';
    if (routeSnapshot.firstChild) {
      title = this.getPageTitle(routeSnapshot.firstChild) || title;
    }
    return title;
  }

  static formatNames(str: string, limit: number = 3): string {
    if (str.includes(',')) {
      const names = str.split(',');
      const displayedNames = names.slice(0, limit).join(', ');
      const remainingCount = names.length - limit;

      return remainingCount > 0 ? `${displayedNames} +${remainingCount}` : displayedNames;
    }
    return str;
  }

  static cleanObjectByRemovingKeysWithoutValue(obj: any): any {
    if (obj && typeof obj === 'object') {
      Object.keys(obj).forEach((key) => {
        if (!obj[key] || obj[key] === '') {
          delete obj[key];
        }
      });
    }
    return obj;
  }

  static calculateTimeDifference(startDate: Date, endDate: Date): number {
    const differenceInMs = endDate.getTime() - startDate.getTime();
    return Math.floor(differenceInMs / (1000 * 60));
  }

  static hasFormValueChanged<T>(initialValue: T | null | undefined, currentValue: T): boolean {
    if (initialValue === null || initialValue === undefined) {
      return false;
    }
    return JSON.stringify(CommonUtils.normalizeFormValue(initialValue)) !== JSON.stringify(CommonUtils.normalizeFormValue(currentValue));
  }

  private static normalizeFormValue(value: any): any {
    if (value === null || value === undefined) return null;
    if (typeof value === 'string' && value.includes('T')) {
      const secondsPart = value.substring(0, 19);
      if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(secondsPart)) {
        return secondsPart;
      }
    }
    if (Array.isArray(value)) return value.map((v) => CommonUtils.normalizeFormValue(v));
    if (typeof value === 'object') {
      const result: Record<string, any> = {};
      for (const key of Object.keys(value)) {
        result[key] = CommonUtils.normalizeFormValue(value[key]);
      }
      return result;
    }
    return value;
  }

  static cloneFormSnapshot<T>(value: T): T {
    return JSON.parse(JSON.stringify(value));
  }

  static b64toBlob = (b64Data: any, contentType = '', fileName: string) => {
    const byteCharacters = atob(b64Data);
    const byteArrays = [];
    const sliceSize = 512;

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);

      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    const blob = new Blob(byteArrays, { type: contentType });
    this.downloadFromBlob(blob, blob.type, fileName);
  };

  static downloadFromBlob(response: Blob, type: string, fileName: string): void {
    const blob = new Blob([response], { type: type });
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    window.URL.revokeObjectURL(url);
  }

  static getAgeFromDob(dob: string): number {
    return moment().diff(moment(dob, moment.ISO_8601), 'years');
  }

  static getAgeGroupFromAge(age: number): number {
    if (age <= 5) return 1;
    if (age > 5 && age <= 8) return 2;
    if (age > 8 && age <= 17) return 3;
    return 4;
  }

  static formatHoursToHM(decimalHours: number): string {
    if (!decimalHours || decimalHours === 0) return '0m';

    const duration = moment.duration(decimalHours, 'hours');
    const hours = Math.floor(duration.asHours());
    const minutes = Math.floor(duration.minutes());

    if (hours === 0) {
      return `${minutes}m`;
    } else if (minutes === 0) {
      return `${hours}h`;
    } else {
      return `${hours}h ${minutes}m`;
    }
  }

  static bankersRound(value: number, decimals: number = 2): number {
    if (value === null || value === undefined || !isFinite(value)) {
      return value;
    }

    const factor = Math.pow(10, decimals);
    const shifted = value * factor;

    const lowerInt = Math.floor(shifted);
    const fractionalPart = shifted - lowerInt;

    const isExactlyHalf = Math.abs(fractionalPart - 0.5) < 1e-9;

    let roundedShifted: number;
    if (isExactlyHalf) {
      const lowerIsEven = lowerInt % 2 === 0;
      roundedShifted = lowerIsEven ? lowerInt : lowerInt + 1;
    } else {
      roundedShifted = Math.round(shifted);
    }

    return roundedShifted / factor;
  }

  static getHoursInRangeQuarter(startTime: string, endTime: string): number {
    const start = moment(new Date(startTime));
    const end = moment(new Date(endTime));
    const diffInMinutes = end.diff(start, 'minutes');
    return Math.round((diffInMinutes / 60) * 4) / 4;
  }

  static getDaysOfWeekBetween(startDate: string, endDate: string): number[] {
    if (!startDate || !endDate) return [];

    const start = moment(startDate);
    const end = moment(endDate);

    if (start.isSame(end, 'day')) {
      return [start.day()];
    }

    const days = new Set<number>();
    const current = start.clone();

    while (current.isSameOrBefore(end, 'day')) {
      days.add(current.day());
      current.add(1, 'day');
    }
    return Array.from(days);
  }

  static getRemainingDays(date: string): number {
    const today = moment();
    const end = moment(date);
    return end.diff(today, 'days');
  }

  static combineDateAndTime(dateStr: string, timeStr: string): string {
    if (!dateStr || !timeStr) {
      return '';
    }
    const date = new Date(dateStr);
    const time = new Date(timeStr);
    date.setHours(time.getHours());
    date.setMinutes(time.getMinutes());
    date.setSeconds(time.getSeconds());
    date.setMilliseconds(time.getMilliseconds());
    return DateUtils.toUTC(date.toISOString(), 'yyyy-MM-DDTHH:mm:ss');
  }

  static isFutureLesson(startTime: string): boolean {
    const now = moment();
    const lessonTime = moment(startTime);
    const lessonMinutes = lessonTime.hours() * 60 + lessonTime.minutes();
    const nowMinutes = now.hours() * 60 + now.minutes();
    return lessonMinutes > nowMinutes;
  }

  static getDependentNameFromValue(value: number | undefined, currentUser: Account | null): string {
    if (currentUser?.dependentDetails?.find((name) => name.id === value)?.firstName) {
      return (
        `${currentUser?.dependentDetails.find((name) => name.id === value)?.firstName || ''} ${currentUser?.dependentDetails.find((name) => name.id === value)?.lastName || ''}` ||
        ''
      );
    } else {
      return `${currentUser?.firstName || ''} ${currentUser?.lastName || ''}` || '';
    }
  }

  static getCreatedByName(currentUser: Account | null): string {
    return currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : 'Self';
  }

  static getChartOptions(availableLeaves: number, usedLeaves: number, totalLeaves: number): EChartsOption {
    // If total hours is 0, show a full completed circle
    if (totalLeaves === 0) {
      return {
        tooltip: {
          trigger: 'item',
          formatter: '{b}: {c}'
        },
        animation: false,
        series: {
          type: 'pie',
          radius: ['70%', '90%'],
          avoidLabelOverlap: false,
          startAngle: 90,
          label: {
            show: true,
            position: 'center',
            formatter: `{b|0/0}`,
            rich: {
              b: {
                fontSize: 18,
                fontWeight: 'normal',
                lineHeight: 30,
                color: 'black',
                fontFamily: 'Nunito Sans'
              }
            }
          },
          data: [
            {
              value: 0,
              name: 'Total Leaves',
              itemStyle: {
                color: Constants.colors.primaryColor
              },
              emphasis: {
                itemStyle: {
                  color: Constants.colors.primaryColor
                }
              }
            }
          ]
        }
      };
    }

    return {
      tooltip: {
        trigger: 'item',
        formatter: '{b}: {c}'
      },
      animation: false,
      series: {
        type: 'pie',
        radius: ['70%', '90%'],
        avoidLabelOverlap: false,
        startAngle: 90,
        label: {
          show: true,
          position: 'center',
          formatter: `{b|${availableLeaves}/${totalLeaves}}`,
          rich: {
            b: {
              fontSize: 18,
              fontWeight: 'normal',
              lineHeight: 30,
              color: 'black',
              fontFamily: 'Nunito Sans'
            }
          }
        },
        data: [
          {
            value: availableLeaves,
            name: 'Available Leaves',
            itemStyle: {
              color: Constants.colors.primaryColor
            },
            emphasis: {
              itemStyle: {
                color: Constants.colors.primaryColor
              }
            }
          },
          {
            value: usedLeaves,
            name: 'Used Leaves',
            itemStyle: {
              color: Constants.colors.headerBgColor
            },
            emphasis: {
              itemStyle: {
                color: Constants.colors.headerBgColor
              }
            }
          }
        ]
      }
    };
  }

  static getWorkingHoursChartOptions(completedHours: number, pendingHours: number, totalHours: number): EChartsOption {
    // If total hours is 0, show a full completed circle
    if (totalHours === 0) {
      return {
        tooltip: {
          trigger: 'item',
          formatter: '{b}: {c}'
        },
        animation: false,
        series: {
          type: 'pie',
          radius: ['70%', '90%'],
          avoidLabelOverlap: false,
          startAngle: 90,
          label: {
            show: true,
            position: 'center',
            formatter: `{b|0m/0m}`,
            rich: {
              b: {
                fontSize: 18,
                fontWeight: 'normal',
                lineHeight: 30,
                color: 'black',
                fontFamily: 'Nunito Sans'
              }
            }
          },
          data: [
            {
              value: 0,
              name: 'Completed Hours',
              itemStyle: {
                color: Constants.colors.primaryColor
              },
              emphasis: {
                itemStyle: {
                  color: Constants.colors.primaryColor
                }
              }
            }
          ]
        }
      };
    }

    // Normal case with non-zero total hours
    return {
      tooltip: {
        trigger: 'item',
        formatter: '{b}: {c}'
      },
      animation: false,
      series: {
        type: 'pie',
        radius: ['70%', '90%'],
        avoidLabelOverlap: false,
        startAngle: 90,
        label: {
          show: true,
          position: 'center',
          formatter: (() => {
            const completedStr = this.formatHoursToHM(completedHours);
            const totalStr = this.formatHoursToHM(totalHours);
            const singleLine = `${completedStr}/${totalStr}`;
            return singleLine.length > 10 ? `{b|${completedStr} /}\n{b|${totalStr}}` : `{b|${singleLine}}`;
          })(),
          rich: {
            b: {
              fontSize: 18,
              fontWeight: 'normal',
              lineHeight: 30,
              color: 'black',
              fontFamily: 'Nunito Sans'
            }
          }
        },
        data: [
          {
            value: completedHours,
            name: 'Completed Hours',
            itemStyle: {
              color: Constants.colors.primaryColor
            },
            emphasis: {
              itemStyle: {
                color: Constants.colors.primaryColor
              }
            }
          },
          {
            value: pendingHours,
            name: 'Pending Hours',
            itemStyle: {
              color: Constants.colors.headerBgColor
            },
            emphasis: {
              itemStyle: {
                color: Constants.colors.headerBgColor
              }
            }
          }
        ]
      }
    };
  }
}
